import { ALL_VEHICLE_EXPIRATION_TYPES_SHALLOW_QUERY } from '@/modules/vehicles/queries'
import type {
  AllVehicleExpirationTypesShallowQuery,
  VehicleExpirationTypesQueryVariables,
} from '@/modules/vehicles/types.graphql'
import { getVehicleExpirationTypeName } from '@/modules/vehicles/util'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps<string> {
  clearable?: boolean
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function VehicleExpirationTypeCombobox({
  mode,
  value,

  placeholder,
  searchPlaceholder,
  noResultsText,
  clearable = true,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    loading: 'common.loading',
    placeholder: 'common.select-ellipsis',
  })

  const { data, loading } = useQuery<
    AllVehicleExpirationTypesShallowQuery,
    VehicleExpirationTypesQueryVariables
  >(ALL_VEHICLE_EXPIRATION_TYPES_SHALLOW_QUERY, {
    variables: { orderBy: 'name' },
  })

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      (data?.allVehicleExpirationTypes.edges ?? []).map(({ node }) => ({
        value: node.id,
        // The built-in types are stored with Norwegian names; this maps them
        // through the same lookup the rest of the fleet UI uses.
        label: getVehicleExpirationTypeName({ name: node.name }),
      })),
    [data?.allVehicleExpirationTypes.edges]
  )

  const resolvedPlaceholder = loading
    ? translations.loading
    : placeholder ?? translations.placeholder

  if (mode === 'single') {
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={items}
        clearable={clearable}
        placeholder={resolvedPlaceholder}
        searchPlaceholder={searchPlaceholder}
        noResultsText={noResultsText}
        onChange={onChange}
      />
    )
  }

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={items}
      placeholder={resolvedPlaceholder}
      searchPlaceholder={searchPlaceholder}
      noResultsText={noResultsText}
      onChange={onChange}
    />
  )
}
